import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAppToast } from "@/hooks/useAppToast";

export interface ProductService {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  code: string | null;
  category: string | null;
  type: "ps" | "mrr";
  item_type: string;
  power_w: number | null;
  cost: number | null;
  unit_price: number;
  unit: string | null;
  supplier_id: string | null;
  contact_id: string | null;
  installer_contact_id: string | null;
  engineer_contact_id: string | null;
  service_type: string | null;
  image_url: string | null;
  weight_kg: number | null;
  area_m2: number | null;
  capacity_panels: number | null;
  max_oversizing_percent: number | null;
  optimal_capacity_percent: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type ProductServiceInsert = Omit<ProductService, "id" | "tenant_id" | "created_at" | "updated_at" | "contact_id" | "installer_contact_id" | "engineer_contact_id" | "service_type"> & {
  contact_id?: string | null;
  installer_contact_id?: string | null;
  engineer_contact_id?: string | null;
  service_type?: string | null;
};

export function useProductsServices() {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ["products-services", currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const { data, error } = await supabase
        .from("products_services")
        .select("*, supplier:suppliers(id, name), contact:contacts!contact_id(id, name), installer:contacts!installer_contact_id(id, name), engineer:contacts!engineer_contact_id(id, name)")
        .eq("tenant_id", currentTenant.id)
        .order("name", { ascending: true });

      if (error) throw error;
      return data as (ProductService & {
        supplier: { id: string; name: string } | null;
        contact: { id: string; name: string } | null;
        installer: { id: string; name: string } | null;
        engineer: { id: string; name: string } | null;
      })[];
    },
    enabled: !!currentTenant?.id,
  });
}

export function useProductServiceMutations() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const createProductService = useMutation({
    mutationFn: async (data: ProductServiceInsert) => {
      if (!currentTenant?.id) throw new Error("No tenant");

      const { error, data: created } = await supabase
        .from("products_services")
        .insert({
          ...data,
          tenant_id: currentTenant.id,
        })
        .select()
        .single();

      if (error) throw error;
      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products-services"] });
      toast.success({ title: "Produto/Serviço criado" });

      // Onboarding tracking (fire-and-forget)
      supabase.rpc("update_checklist_item", { p_item_key: "products_added", p_value: true }).then(() => {
        queryClient.invalidateQueries({ queryKey: ["onboarding-checklist"] });
      }, () => {});
    },
    onError: (error: Error) => {
      toast.error({ title: "Erro ao criar", description: error.message });
    },
  });

  const updateProductService = useMutation({
    mutationFn: async ({ id, ...data }: Partial<ProductService> & { id: string }) => {
      const { error } = await supabase
        .from("products_services")
        .update(data)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products-services"] });
      toast.success({ title: "Produto/Serviço atualizado" });
    },
    onError: (error: Error) => {
      toast.error({ title: "Erro ao atualizar", description: error.message });
    },
  });

  const deleteProductService = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("products_services")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products-services"] });
      toast.success({ title: "Produto/Serviço removido" });
    },
    onError: (error: Error) => {
      toast.error({ title: "Erro ao remover", description: error.message });
    },
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from("products_services")
        .update({ is_active })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: (_, { is_active }) => {
      queryClient.invalidateQueries({ queryKey: ["products-services"] });
      toast.success({ title: is_active ? "Produto ativado" : "Produto desativado" });
    },
    onError: (error: Error) => {
      toast.error({ title: "Erro ao alterar status", description: error.message });
    },
  });

  return {
    createProductService,
    updateProductService,
    deleteProductService,
    toggleActive,
  };
}
